import React from 'react';
import { FileMenuBar } from './FileMenuBar';
import {
  BookMarkedIcon,
  FilmIcon,
  FolderIcon,
  UsersIcon,
} from './icons';
import { AppState, Dogma } from '../types';
import { VideoSettings } from '../types/videoSettings';
import { Project } from '../types/project';

interface StudioHeaderProps {
  // Project
  activeProject: Project | null;
  onOpenProjectManager: () => void;

  // App State
  appState: AppState;
  onCancelGeneration: () => void;

  // Video Provider
  videoSettings: VideoSettings;

  // Config Toggle
  showStudioConfig: boolean;
  onToggleConfig: () => void;

  // Manager Triggers
  onOpenShotLibrary: () => void;
  onOpenCharacterManager: () => void;
  onOpenDogmaManager: () => void;
  activeDogma: Dogma | null;
  onVideoSettingsChange: (settings: VideoSettings) => void;
}

export const StudioHeader: React.FC<StudioHeaderProps> = ({
  activeProject,
  onOpenProjectManager,
  appState,
  onCancelGeneration,
  videoSettings,
  showStudioConfig,
  onToggleConfig,
  onOpenShotLibrary,
  onOpenCharacterManager,
  onOpenDogmaManager,
  activeDogma,
  onVideoSettingsChange,
}) => {
  return (
    <div className="flex items-center gap-4 px-4 py-2 w-full h-full">
      <FileMenuBar onOpenProjectManager={onOpenProjectManager} />

      {/* Model Selectors */}
      <div className="flex items-center gap-2 px-3 py-1 bg-gray-800 rounded-full border border-gray-700 ml-2">
        {/* Video Model Selector */}
        <div className="w-px h-3 bg-gray-600 mx-1"></div>
        <div className="flex items-center gap-1">
          <span className="text-[10px] uppercase text-gray-500 font-bold">Moteur vidéo:</span>
          <select
            value={videoSettings.engine}
            onChange={(e) => onVideoSettingsChange({ ...videoSettings, engine: e.target.value as any })}
            className="text-xs text-indigo-400 font-medium px-2 py-0.5 bg-indigo-500/10 rounded border border-indigo-500/20 outline-none focus:border-indigo-500 cursor-pointer appearance-none"
            style={{ backgroundImage: 'none' }} // Remove default arrow if desired, or keep it
          >
            <option value="veo">Veo (Google)</option>
            <option value="ltx2_fal">LTX-2 (Fal.ai)</option>
            {/* Add other engines if available in types */}
          </select>
        </div>
      </div>
      {
        activeProject ? (
          <div className="flex items-center gap-2 px-3 py-1 bg-gray-800/50 rounded-lg border border-gray-700/50">
            <FolderIcon className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-sm font-medium text-gray-200">{activeProject.name}</span>
          </div>
        ) : (
          <button
            onClick={onOpenProjectManager}
            className="flex items-center gap-2 px-3 py-1 bg-indigo-600/20 hover:bg-indigo-600/40 text-indigo-300 border border-indigo-500/30 rounded-lg transition-colors animate-pulse"
          >
            <FolderIcon className="w-3.5 h-3.5" />
            <span className="text-sm font-medium">Créer/Ouvrir Projet</span>
          </button>
        )
      }
      {
        appState === AppState.LOADING && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600/20 border border-indigo-500/30 rounded-lg animate-pulse">
            <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-xs text-indigo-300 font-medium">Génération vidéo...</span>
            <button onClick={onCancelGeneration} className="text-xs text-red-400 hover:text-red-300 ml-1 px-1.5 py-0.5 rounded hover:bg-red-500/10">✕</button>
          </div>
        )
      }
      <div className="flex-1" />
      <button
        onClick={onToggleConfig}
        className={`p-1.5 rounded-lg transition-colors border ${showStudioConfig ? 'bg-indigo-600 border-indigo-500 text-white' : 'bg-transparent border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800'}`}
        title={showStudioConfig ? "Masquer la configuration" : "Afficher la configuration Studio"}
      >
        <span className="material-symbols-outlined text-lg">tune</span>
      </button>
      <div className="flex items-center gap-1 ml-2 border-l border-gray-700 pl-3" data-tour="context-managers">
        <button onClick={onOpenShotLibrary} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-400 hover:text-white hover:bg-gray-800 rounded-md transition-colors" title="Bibliothèque de Plans" data-tour="bin-manager-trigger">
          <BookMarkedIcon className="w-4 h-4" />
          <span>Bibliothèque</span>
        </button>
        <button onClick={onOpenCharacterManager} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-400 hover:text-white hover:bg-gray-800 rounded-md transition-colors" title="Gestionnaire de Personnages" data-tour="character-manager-trigger">
          <UsersIcon className="w-4 h-4" />
          <span>Caractères</span>
        </button>
        <button onClick={onOpenDogmaManager} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-400 hover:text-white hover:bg-gray-800 rounded-md transition-colors" title="Gestionnaire de Dogmas" data-tour="dogma-selector">
          <FilmIcon className="w-4 h-4" />
          <span>Dogmas</span>
          {activeDogma && <span className="w-2 h-2 rounded-full bg-green-500" title={`Actif: ${activeDogma.title}`}></span>}
        </button>
      </div>
    </div >
  );
};

export default StudioHeader;
